import React from 'react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { OAuthDisclosure } from './OAuthDisclosure';

interface SocialLoginProps {
    onEmailLoginClick: () => void;
}

export const SocialLogin: React.FC<SocialLoginProps> = ({ onEmailLoginClick }) => {
    const { signInWithGoogle, signInWithFacebook, isAdmin } = useAuth();

    return (
        <div className="flex flex-col gap-4 w-full max-w-sm mx-auto" dir="rtl">
            {/* Branding Header */}
            <div className="flex flex-col items-center justify-center mb-4">
                <div className="relative mb-4">
                    <img
                        src="/assets/flask-only-no-bg.png"
                        alt="Spirit Research Lab"
                        className="w-20 h-20 object-contain drop-shadow-[0_0_24px_rgba(251,191,36,0.45)]"
                    />
                </div>
                <h2 className="text-2xl font-bold text-gold-400 tracking-tight">כניסה למערכת</h2>
                <p className="text-sm text-mystic-300 mt-1">יש לבחור חשבון</p>
            </div>

            {/* Google Button */}
            <button
                onClick={signInWithGoogle}
                className="flex items-center justify-center gap-3 w-full px-4 py-3.5
                           bg-white/5 text-white border border-white/10
                           rounded-2xl hover:bg-white/10 hover:-translate-y-0.5
                           active:scale-95 transition-all duration-300 font-medium
                           shadow-lg shadow-black/20"
            >
                <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                <span>לכניסה עם חשבון Google</span>
            </button>

            {/* Facebook Button */}
            <button
                onClick={signInWithFacebook}
                className="flex items-center justify-center gap-3 w-full px-4 py-3.5
                           bg-blue-900/30 text-blue-200 border border-blue-400/20
                           rounded-2xl hover:bg-blue-800/40 hover:-translate-y-0.5
                           active:scale-95 transition-all duration-300 font-medium
                           shadow-lg shadow-black/20"
            >
                <svg className="w-5 h-5 shrink-0 fill-current" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
                <span>לכניסה עם חשבון Facebook</span>
            </button>

            {/* Email Login Link — admin only (testing purposes) */}
            {(isAdmin ||
                import.meta.env.DEV ||
                import.meta.env.MODE === 'test' ||
                import.meta.env.VITE_ALLOW_EMAIL_LOGIN === 'true') && (
                    <>
                        <div className="flex items-center gap-3 my-1">
                            <div className="flex-1 h-px bg-white/10" />
                            <span className="text-xs text-mystic-500">או</span>
                            <div className="flex-1 h-px bg-white/10" />
                        </div>

                        <button
                            onClick={onEmailLoginClick}
                            className="text-sm text-mystic-400 hover:text-gold-400 transition-colors duration-200
                                   underline-offset-2 hover:underline text-center"
                        >
                            לכניסה עם אימייל
                        </button>
                    </>
                )}

            {/* Disclosure — visible to all */}
            <OAuthDisclosure />
        </div>
    );
};
